/**
 * Google Apps Script Integration
 * Writes booking data to Google Sheets via Apps Script Web App
 */

import { SimplifiedBooking } from '../types/booking'

// APPS SCRIPT WEB APP URL
// Replace this with your deployed Apps Script URL after setup
const APPS_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbx7hOb6jkUqE2z7QoS7fz0INFsAn2cg5UmcEgNQPd3IIlSOpY0_xU2Anu9rzQpuVl8PWA/exec'

export interface BookingSubmission {
  userId: string
  userName: string
  startDate: string // DD/MM/YYYY format
  endDate: string // DD/MM/YYYY format
  location: string
  status: 'active' | 'upcoming' | 'completed' | 'maintenance'
  notes?: string
  firstName?: string
  surname?: string
  farm?: string
  email?: string
  phone?: string
  pickupMethod?: 'farm-pickup' | 'wmf-pickup'
  pickupFrom?: string
  dropoffMethod?: 'farm-dropoff' | 'wmf-dropoff' | 'pending'
  dropoffTo?: string
}

/**
 * Submits a new booking to Google Sheets via Apps Script
 */
export async function submitBooking(
  booking: BookingSubmission,
): Promise<{ success: boolean; message: string; id?: string }> {
  // Check if Apps Script URL is configured
  if (APPS_SCRIPT_URL === 'YOUR_APPS_SCRIPT_URL_HERE') {
    return {
      success: false,
      message:
        'Apps Script URL not configured. Please update utils/appsScript.ts with your deployed Web App URL.',
    }
  }

  try {
    console.log('Submitting booking:', booking)

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors', // Important for Apps Script
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(booking),
    })

    // Note: With no-cors mode, we can't read the response
    // Apps Script will still process the request
    console.log('Booking submitted successfully (no-cors mode)')

    return {
      success: true,
      message: 'Booking submitted successfully',
    }
  } catch (error) {
    console.error('Error submitting booking:', error)
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Failed to submit booking. Please check your internet connection and try again.',
    }
  }
}

/**
 * Validates booking data before submission
 */
export function validateBooking(booking: BookingSubmission): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!booking.userName || booking.userName.trim() === '') {
    errors.push('User name is required')
  }

  if (!booking.startDate || !booking.endDate) {
    errors.push('Start and end dates are required')
  }

  if (!booking.location || booking.location.trim() === '') {
    errors.push('Location is required')
  }

  if (
    !['active', 'upcoming', 'completed', 'maintenance'].includes(booking.status)
  ) {
    errors.push('Invalid status')
  }

  // Validate date format (DD/MM/YYYY)
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
  if (booking.startDate && !dateRegex.test(booking.startDate)) {
    errors.push('Start date must be in DD/MM/YYYY format')
  }
  if (booking.endDate && !dateRegex.test(booking.endDate)) {
    errors.push('End date must be in DD/MM/YYYY format')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
